import React, { useEffect, useState, useRef } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import BlockEditor from '../components/BlockEditor';
import { blockHasDisplayableContent } from '../utils/facebookVideoEmbed';
import { useAuth } from '../contexts/AuthContext';
import { logAction, SECTIONS, ACTIONS } from '../services/auditLog';
import { storagePublicBaseUrl } from '../config/env';
import './NewsManagement.css';
import { getHomePlProxyHeaders } from '../utils/proxyUploadHeaders';

// Upload: token sesji (X-Proxy-Token) lub bezpośrednio upload.php (X-Upload-Token). Fallback Supabase.
async function uploadImageToHome(file, type = 'news') {
  const uploadUrl = process.env.REACT_APP_UPLOAD_URL?.trim();
  if (!uploadUrl) {
    return uploadImageToSupabase(file, 'news');
  }
  const headers = getHomePlProxyHeaders();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers,
    body: formData,
  });
  const text = await response.text();
  if (!response.ok) {
    console.error('home.pl upload failed:', response.status, text);
    throw new Error(`Upload failed: ${response.status}`);
  }
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    const lastBrace = text.lastIndexOf('{');
    if (lastBrace !== -1) {
      try {
        data = JSON.parse(text.slice(lastBrace));
      } catch (e2) {
        throw new Error('Serwer zwrócił nieprawidłową odpowiedź. Sprawdź REACT_APP_UPLOAD_URL i logi PHP.');
      }
    } else {
      throw new Error('Serwer zwrócił nieprawidłową odpowiedź.');
    }
  }
  if (!data.url) throw new Error('Upload response missing url');
  return data.url;
}

async function uploadImageToSupabase(file, type) {
  const bucket = type === 'events' ? 'event-images' : 'news-images';
  const ext = (file.name && file.name.split('.').pop()) || 'jpg';
  const fileName = `gallery/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`;
  const { error: uploadError } = await supabaseAdmin.storage
    .from(bucket)
    .upload(fileName, file, { contentType: file.type || 'image/jpeg', upsert: false });
  if (uploadError) {
    console.error('Supabase upload error:', uploadError);
    throw new Error(uploadError.message || 'Błąd przesyłania do Supabase');
  }
  const { data: { publicUrl } } = supabaseAdmin.storage.from(bucket).getPublicUrl(fileName);
  return publicUrl;
}

async function deleteImagesOnHome(urls) {
  if (!urls || urls.length === 0) return;
  const deleteUrl = process.env.REACT_APP_DELETE_URL;
  const headers = getHomePlProxyHeaders(true);
  const response = await fetch(deleteUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({ urls }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`delete.php failed: ${response.status} ${text}`);
  }
}

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function validateImageFile(file) {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'File type is not allowed. Only JPG, PNG and WebP are accepted.';
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return 'File is too large. Maximum size is 5 MB.';
  }
  return null;
}

// Helper function to convert relative image path to full public URL
function getPublicImageUrl(path) {
  if (!path) return null;
  // If already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // Convert relative path to full public URL
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  // Use bucket "news-images" with folder "gallery/"
  return `${storagePublicBaseUrl}/storage/v1/object/public/news-images/${cleanPath}`;
}

// Wyciąga z bloków wszystkie URL-e zdjęć z tokenów [image:url...]
function getContentImageUrlsFromBlocks(blocks) {
  if (!blocks || !Array.isArray(blocks)) return [];
  const urls = [];
  const re = /\[image:(https?:\/\/[^|\]]+)(?:\|[^\]]*)?\]/g;
  blocks.forEach((block) => {
    if (block.type === 'text' && block.text) {
      let m;
      while ((m = re.exec(block.text)) !== null) urls.push(m[1]);
    }
  });
  return urls;
}

const AUTOSAVE_INTERVAL_MS = 30 * 1000; // 30 sekund

export default function NewsManagement() {
  const { user, isAdmin } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [listTab, setListTab] = useState('all'); // 'all' | 'drafts'
  const [autosaveDraftId, setAutosaveDraftId] = useState(null); // id wiersza utworzonego przez autosave (tylko przy nowej aktualności)
  const [sendPushNotification, setSendPushNotification] = useState(true);
  const [selectPushRecipients, setSelectPushRecipients] = useState(false);
  const [selectedPushUserIds, setSelectedPushUserIds] = useState([]);
  const [pushRecipientsList, setPushRecipientsList] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    status: 'draft',
  });
  const [blocks, setBlocks] = useState([{ 
    type: 'text', 
    text: '', 
    align: 'left',
    bold: false,
    italic: false,
    underline: false
  }]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);
  const [removedImageUrls, setRemovedImageUrls] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const formDataRef = useRef(formData);
  const blocksRef = useRef(blocks);
  const galleryFilesRef = useRef(galleryFiles);
  const existingGalleryImagesRef = useRef(existingGalleryImages);
  const removedImageUrlsRef = useRef(removedImageUrls);
  const sendPushNotificationRef = useRef(sendPushNotification);
  const selectPushRecipientsRef = useRef(selectPushRecipients);
  const selectedPushUserIdsRef = useRef(selectedPushUserIds);
  const lastChangeAtRef = useRef(0);
  const lastAutosaveAtRef = useRef(0);
  const galleryInputRef = useRef(null);
  formDataRef.current = formData;
  blocksRef.current = blocks;
  galleryFilesRef.current = galleryFiles;
  existingGalleryImagesRef.current = existingGalleryImages;
  removedImageUrlsRef.current = removedImageUrls;
  sendPushNotificationRef.current = sendPushNotification;
  selectPushRecipientsRef.current = selectPushRecipients;
  selectedPushUserIdsRef.current = selectedPushUserIds;

  useEffect(() => {
    loadNews();
  }, []);

  useEffect(() => {
    if (!showModal || !isAdmin || !selectPushRecipients) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabaseAdmin
        .from('users')
        .select('id, device_id, expo_push_token')
        .not('expo_push_token', 'is', null)
        .order('created_at', { ascending: false });
      if (!cancelled && !error) setPushRecipientsList(data || []);
    })();
    return () => { cancelled = true; };
  }, [showModal, isAdmin, selectPushRecipients]);

  const displayedNews = listTab === 'drafts' ? news.filter((n) => n.status === 'draft') : news.filter((n) => n.status === 'published');

  // Log editor render for debugging
  useEffect(() => {
    console.log('EDITOR RENDERED');
  });

  const loadNews = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('news')
        .select(`
          *,
          news_images (*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNews(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania aktualności');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Reorder gallery images - move image up
  const moveGalleryImageUp = (index, isExisting) => {
    if (isExisting) {
      if (index === 0) return; // Already at top
      const newImages = [...existingGalleryImages];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      setExistingGalleryImages(newImages);
    } else {
      if (index === 0) return; // Already at top
      const newPreviews = [...galleryPreviews];
      [newPreviews[index - 1], newPreviews[index]] = [newPreviews[index], newPreviews[index - 1]];
      setGalleryPreviews(newPreviews);
      const newFiles = [...galleryFiles];
      [newFiles[index - 1], newFiles[index]] = [newFiles[index], newFiles[index - 1]];
      setGalleryFiles(newFiles);
    }
  };

  // Reorder gallery images - move image down
  const moveGalleryImageDown = (index, isExisting) => {
    if (isExisting) {
      if (index === existingGalleryImages.length - 1) return; // Already at bottom
      const newImages = [...existingGalleryImages];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      setExistingGalleryImages(newImages);
    } else {
      if (index === galleryPreviews.length - 1) return; // Already at bottom
      const newPreviews = [...galleryPreviews];
      [newPreviews[index], newPreviews[index + 1]] = [newPreviews[index + 1], newPreviews[index]];
      setGalleryPreviews(newPreviews);
      const newFiles = [...galleryFiles];
      [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
      setGalleryFiles(newFiles);
    }
  };

  // Upload image for content editor – zapis na home.pl w public_html/uploads/news (jak galeria)
  const uploadImage = async (file) => {
    const validationError = validateImageFile(file);
    if (validationError) throw new Error(validationError);
    return uploadImageToHome(file, 'news');
  };

  const handleGallerySelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const invalidReasons = [];

    files.forEach(file => {
      const err = validateImageFile(file);
      if (err) {
        invalidReasons.push(`${file.name}: ${err}`);
        return;
      }
      validFiles.push(file);
    });

    if (invalidReasons.length > 0) {
      toast.error(invalidReasons[0] + (invalidReasons.length > 1 ? ` (and ${invalidReasons.length - 1} more)` : ''));
    }

    if (validFiles.length > 0) {
      setGalleryFiles([...galleryFiles, ...validFiles]);

      // Preview wszystkich plików
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setGalleryPreviews(prev => [...prev, { file, preview: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeGalleryFile = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  // Mark existing image for removal on SAVE (no DB or home.pl delete until submit)
  const markGalleryImageForRemoval = (imagePath) => {
    setRemovedImageUrls((prev) => [...prev, imagePath]);
    setExistingGalleryImages((prev) => prev.filter((img) => img.image_path !== imagePath));
  };

  // Convert blocks to HTML for backward compatibility (content field)
  function blocksToHtml(blocks) {
    if (!blocks || blocks.length === 0) return '';
    
    return blocks.map(block => {
      if (block.type === 'text') {
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        // Token [divider] w treści → <hr/> między akapitami; [sup]...[/sup] i [sub]...[/sub] → indeks górny/dolny
        const parts = (block.text || '').split('[divider]');
        return parts.map((part) => {
          let processed = part
            .replace(/\[sup\]([\s\S]*?)\[\/sup\]/g, '<sup>$1</sup>')
            .replace(/\[sub\]([\s\S]*?)\[\/sub\]/g, '<sub>$1</sub>');
          if (block.bold) processed = `<strong>${processed}</strong>`;
          if (block.italic) processed = `<em>${processed}</em>`;
          if (block.underline) processed = `<u>${processed}</u>`;
          return `<p ${alignStyle}>${processed}</p>`;
        }).join('<hr/>');
      } else if (block.type === 'image') {
        const url = block.url || '';
        const width = block.width || '100%';
        const align = block.align || 'left';
        const widthStyle = width === '100%' ? 'max-width: 100%; width: 100%;' : `max-width: ${width}; width: ${width};`;
        const alignStyle = `text-align: ${align};`;
        return `<div style="${alignStyle}"><img src="${url}" style="${widthStyle} height: auto; display: ${width === '100%' ? 'block' : 'inline-block'}; margin: 8px 0;" /></div>`;
      } else if (block.type === 'divider') {
        return '<hr/>';
      } else if (block.type === 'link') {
        const text = block.text || '';
        const url = block.url || '#';
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        // Only include link if URL is valid
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
          return `<p ${alignStyle}><a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a></p>`;
        }
        return `<p ${alignStyle}>${text}</p>`;
      } else if (block.type === 'facebookVideo' && block.url) {
        return `<p data-facebook-video="1">[Film z Facebooka]</p>`;
      }
      return '';
    }).join('');
  }

  // Convert HTML/content to blocks (for backward compatibility)
  function htmlToBlocks(html) {
    if (!html) return [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }];
    
    // Extract text and formatting from HTML
    let plainText = html;
    
    // Extract bold
    const hasBold = /<strong>|<b>/i.test(plainText);
    plainText = plainText.replace(/<\/?strong>|<\/?b>/gi, '');
    
    // Extract italic
    const hasItalic = /<em>|<i>/i.test(plainText);
    plainText = plainText.replace(/<\/?em>|<\/?i>/gi, '');
    
    // Extract underline
    const hasUnderline = /<u>/i.test(plainText);
    plainText = plainText.replace(/<\/?u>/gi, '');
    
    // Remove all remaining HTML tags
    plainText = plainText.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
    
    if (!plainText) return [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }];
    
    // Try to extract alignment from HTML if present
    let align = 'left';
    const alignMatch = html.match(/text-align:\s*(left|center|right)/i);
    if (alignMatch) {
      align = alignMatch[1].toLowerCase();
    }
    
    // Check if this is a link (has <a> tag)
    const hasLink = /<a\s+href=["']([^"']+)["']/i.test(html);
    if (hasLink) {
      const linkMatch = html.match(/<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/i);
      if (linkMatch) {
        return [{ 
          type: 'link', 
          text: linkMatch[2].trim() || plainText, 
          url: linkMatch[1] || '',
          align: align
        }];
      }
    }
    
    return [{ 
      type: 'text', 
      text: plainText, 
      align: align,
      bold: hasBold,
      italic: hasItalic,
      underline: hasUnderline
    }];
  }

  const resetModalState = () => {
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    setShowModal(false);
    setEditingNews(null);
    setAutosaveDraftId(null);
    setFormData({ title: '', status: 'draft' });
    setBlocks([{ type: 'text', text: '', align: 'left', bold: false, italic: false, underline: false }]);
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setExistingGalleryImages([]);
    setRemovedImageUrls([]);
    setSendPushNotification(true);
    setSelectPushRecipients(false);
    setSelectedPushUserIds([]);
  };

  const hasFormContent = () => {
    if ((formData.title || '').trim()) return true;
    return blocks.some(blockHasDisplayableContent);
  };

  const autosaveDraftLightRef = useRef(null);
  autosaveDraftLightRef.current = async () => {
    if (!showModal || uploadingGallery) return;
    const fd = formDataRef.current;
    const bl = blocksRef.current;
    const galleryFilesCurrent = galleryFilesRef.current;
    const existingCurrent = existingGalleryImagesRef.current;
    const removedCurrent = removedImageUrlsRef.current;
    const htmlFromBlocks = blocksToHtml(bl);
    const cleanHtml = DOMPurify.sanitize(htmlFromBlocks, {
      ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'p', 'br', 'hr'],
      ALLOWED_ATTR: ['style'],
    });
    const title = (fd.title || '').trim() || '(Bez tytułu)';
    let newsId = null;
    try {
      if (editingNews) {
        // Edytujemy opublikowaną aktualność → zapis zawsze do wersji roboczej (szkic), nie nadpisuj opublikowanego.
        if (editingNews.status === 'published') {
          const pushPayloadAutosave = {
            send_push_on_publish: sendPushNotificationRef.current,
            push_recipient_ids: (isAdmin && selectPushRecipientsRef.current && selectedPushUserIdsRef.current?.length > 0) ? selectedPushUserIdsRef.current : null,
          };
          const { data: existingDraft } = await supabaseAdmin
            .from('news')
            .select('id')
            .eq('parent_id', editingNews.id)
            .eq('status', 'draft')
            .limit(1)
            .maybeSingle();
          if (existingDraft) {
            const { error } = await supabaseAdmin
              .from('news')
              .update({
                title,
                content: cleanHtml,
                blocks: bl,
                updated_at: new Date().toISOString(),
                ...pushPayloadAutosave,
              })
              .eq('id', existingDraft.id);
            if (error) throw error;
            newsId = existingDraft.id;
          } else {
            const { data: inserted, error } = await supabaseAdmin
              .from('news')
              .insert({
                title,
                content: cleanHtml,
                blocks: bl || [],
                alignment: 'left',
                status: 'draft',
                parent_id: editingNews.id,
                ...pushPayloadAutosave,
              })
              .select('id')
              .single();
            if (error) throw error;
            if (inserted?.id) {
              newsId = inserted.id;
            }
          }
        } else {
          // Edytujemy szkic (lub wiersz bez parent_id) – zapis w miejscu. Zawsze zostaw status draft.
          const { error } = await supabaseAdmin
            .from('news')
            .update({
              title,
              content: cleanHtml,
              blocks: bl,
              updated_at: new Date().toISOString(),
              status: 'draft',
            })
            .eq('id', editingNews.id);
          if (error) throw error;
          newsId = editingNews.id;
        }
      } else {
        if (autosaveDraftId) {
          const { error } = await supabaseAdmin
            .from('news')
            .update({
              title,
              content: cleanHtml,
              blocks: bl,
              updated_at: new Date().toISOString(),
              status: 'draft',
            })
            .eq('id', autosaveDraftId);
          if (error) throw error;
          newsId = autosaveDraftId;
        } else {
          const { data, error } = await supabaseAdmin
            .from('news')
            .insert({
              title,
              content: cleanHtml || '',
              blocks: bl || [],
              alignment: 'left',
              status: 'draft',
            })
            .select('id')
            .single();
          if (error) throw error;
          if (data?.id) {
            newsId = data.id;
            setAutosaveDraftId(data.id);
          }
        }
      }
      // Usuń zdjęcia oznaczone do usunięcia (edycja)
      if (editingNews && removedCurrent.length > 0) {
        const urlsToDelete = removedCurrent.filter((p) => p && (p.startsWith('http://') || p.startsWith('https://')));
        if (urlsToDelete.length > 0) await deleteImagesOnHome(urlsToDelete);
        const { error: removeError } = await supabaseAdmin
          .from('news_images')
          .delete()
          .eq('news_id', newsId)
          .in('image_path', removedCurrent);
        if (removeError) throw new Error(removeError.message || 'Błąd usuwania zdjęć z galerii');
      }
      // Upload galerii i zapis do news_images (tak jak przy normalnym zapisie)
      if (newsId && (galleryFilesCurrent.length > 0 || existingCurrent.length > 0)) {
        setUploadingGallery(true);
        try {
          const uploadedUrls = [];
          for (let i = 0; i < galleryFilesCurrent.length; i++) {
            const url = await uploadImageToHome(galleryFilesCurrent[i], 'news');
            uploadedUrls.push(url);
          }
          const { error: deleteError } = await supabaseAdmin
            .from('news_images')
            .delete()
            .eq('news_id', newsId);
          if (deleteError) throw new Error(deleteError.message || 'Błąd usuwania starych obrazów');
          const allImagePaths = [
            ...existingCurrent.map((img) => img.image_path),
            ...uploadedUrls,
          ];
          if (allImagePaths.length > 0) {
            const galleryUploads = allImagePaths.map((imagePath) => ({
              news_id: newsId,
              image_path: imagePath,
            }));
            const { error: galleryError } = await supabaseAdmin
              .from('news_images')
              .insert(galleryUploads)
              .select();
            if (galleryError) throw new Error(`Błąd zapisywania galerii: ${galleryError.message || 'Nieznany błąd'}`);
            setExistingGalleryImages([
              ...existingCurrent.filter((img) => !removedCurrent.includes(img.image_path)),
              ...uploadedUrls.map((path) => ({ id: null, image_path: path })),
            ]);
            setGalleryFiles([]);
            setGalleryPreviews([]);
            setRemovedImageUrls([]);
          }
        } catch (galleryErr) {
          console.error('Autosave gallery error:', galleryErr);
          toast.error(galleryErr.message || 'Błąd podczas zapisywania galerii w autosave');
          throw galleryErr;
        } finally {
          setUploadingGallery(false);
        }
      }
      lastAutosaveAtRef.current = Date.now();
      toast.success('Zapisano w roboczych', { duration: 2000 });
      loadNews();
    } catch (err) {
      console.error('Autosave error:', err);
      toast.error('Autosave nie powiódł się: ' + (err.message || 'nieznany błąd'));
    }
  };

  useEffect(() => {
    if (!showModal) return;
    const t = setInterval(() => { autosaveDraftLightRef.current?.(); }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [showModal, uploadingGallery, editingNews?.id, autosaveDraftId]);

  useEffect(() => {
    if (showModal && !editingNews && galleryInputRef.current) {
      galleryInputRef.current.value = '';
    }
  }, [showModal, editingNews]);

  /** Zapisuje wersję roboczą powiązaną z opublikowaną aktualnością (parent_id). Nie zmienia statusu opublikowanego. */
  const saveDraftRevisionForPublished = async (publishedId) => {
    const fd = formDataRef.current;
    const bl = blocksRef.current;
    const existingCurrent = existingGalleryImagesRef.current;
    const galleryFilesCurrent = galleryFilesRef.current;
    const removedCurrent = removedImageUrlsRef.current;
    const htmlFromBlocks = blocksToHtml(bl);
    const cleanHtml = DOMPurify.sanitize(htmlFromBlocks, {
      ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'p', 'br', 'hr'],
      ALLOWED_ATTR: ['style'],
    });
    const title = (fd.title || '').trim() || '(Bez tytułu)';
    const { data: existingDraft } = await supabaseAdmin
      .from('news')
      .select('id')
      .eq('parent_id', publishedId)
      .eq('status', 'draft')
      .limit(1)
      .maybeSingle();
    let draftId;
    const pushPayload = {
      send_push_on_publish: sendPushNotificationRef.current,
      push_recipient_ids: (isAdmin && selectPushRecipientsRef.current && selectedPushUserIdsRef.current?.length > 0) ? selectedPushUserIdsRef.current : null,
    };
    if (existingDraft) {
      const { error } = await supabaseAdmin
        .from('news')
        .update({
          title,
          content: cleanHtml,
          blocks: bl,
          updated_at: new Date().toISOString(),
          ...pushPayload,
        })
        .eq('id', existingDraft.id);
      if (error) throw error;
      draftId = existingDraft.id;
    } else {
      const { data: inserted, error } = await supabaseAdmin
        .from('news')
        .insert({
          title,
          content: cleanHtml,
          blocks: bl || [],
          alignment: 'left',
          status: 'draft',
          parent_id: publishedId,
          ...pushPayload,
        })
        .select('id')
        .single();
      if (error) throw error;
      draftId = inserted?.id;
    }
    if (!draftId) return;
    if (galleryFilesCurrent.length > 0 || existingCurrent.length > 0) {
      const uploadedUrls = [];
      for (let i = 0; i < galleryFilesCurrent.length; i++) {
        const url = await uploadImageToHome(galleryFilesCurrent[i], 'news');
        uploadedUrls.push(url);
      }
      await supabaseAdmin.from('news_images').delete().eq('news_id', draftId);
      const allImagePaths = [
        ...existingCurrent.filter((img) => !removedCurrent.includes(img.image_path)).map((img) => img.image_path),
        ...uploadedUrls,
      ];
      if (allImagePaths.length > 0) {
        await supabaseAdmin
          .from('news_images')
          .insert(allImagePaths.map((imagePath) => ({ news_id: draftId, image_path: imagePath })));
      }
    }
    resetModalState();
    loadNews();
  };

  const handleCloseModal = async () => {
    if (uploadingGallery) return;
    if (!hasFormContent()) {
      if (autosaveDraftId) {
        await supabaseAdmin.from('news').delete().eq('id', autosaveDraftId);
      }
      resetModalState();
      loadNews();
      return;
    }
    if (lastAutosaveAtRef.current > 0 && lastChangeAtRef.current <= lastAutosaveAtRef.current) {
      resetModalState();
      loadNews();
      return;
    }
    if (window.confirm('Czy zapisać w roboczych?')) {
      try {
        // Edytowana jest opublikowana aktualność – zapisz tylko wersję roboczą (szkic), nie zmieniaj statusu opublikowanego.
        if (editingNews?.status === 'published') {
          await saveDraftRevisionForPublished(editingNews.id);
        } else {
          await handleSubmitInternal({ preventDefault: () => {} }, 'draft');
        }
      } catch (err) {
        toast.error(err.message || 'Błąd zapisu');
      }
    } else {
      if (autosaveDraftId && !editingNews) {
        await supabaseAdmin.from('news').delete().eq('id', autosaveDraftId);
      }
      resetModalState();
      loadNews();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmitInternal(e);
  };

  const handleSubmitInternal = async (e, forceStatus) => {
    e.preventDefault();
    const isDraftSave = forceStatus === 'draft';
    const saveStatus = forceStatus ?? formData.status;
    if (saveStatus === 'published' && sendPushNotification && isAdmin && selectPushRecipients && selectedPushUserIds.length === 0) {
      toast.error('Zaznacz co najmniej jednego odbiorcę push lub wyłącz „Wybierz odbiorców”');
      return;
    }
    try {
      // Zapis wersji roboczej przy edycji opublikowanej – zapisz tylko szkic (parent_id), nie zmieniaj opublikowanego.
      if (isDraftSave && editingNews?.status === 'published') {
        await saveDraftRevisionForPublished(editingNews.id);
        return;
      }
      // Convert blocks to HTML for backward compatibility
      const htmlFromBlocks = blocksToHtml(blocks);
      // Sanitize and validate content (ONLY at save time)
      const cleanHtml = DOMPurify.sanitize(htmlFromBlocks, {
        ALLOWED_TAGS: [
          'b','strong','i','em','u',
          'p','br','hr'
        ],
        ALLOWED_ATTR: ['style']
      });
      const sanitizedContent = cleanHtml;
      
      // Validate blocks (przy zapisie do roboczych można zapisać bez treści)
      const validBlocks = blocks.filter(blockHasDisplayableContent);
      
      if (!isDraftSave && validBlocks.length === 0) {
        toast.error('Dodaj treść (tekst, film z Facebooka lub inny blok)');
        return;
      }
      
      // Check character limit
      const textContent = sanitizedContent.replace(/<[^>]*>/g, '');
      if (textContent.length > 10000) {
        toast.error('Treść jest za długa. Maksymalnie 10 000 znaków.');
        return;
      }

      const titleToSave = (formData.title || '').trim() || (isDraftSave ? '(Bez tytułu)' : '');
      const hasPublishableBody = validBlocks.length > 0;
      if (!isDraftSave && (!titleToSave || !hasPublishableBody)) {
        toast.error('Wypełnij wszystkie wymagane pola');
        return;
      }

      // On EDIT: delete removed gallery images from home.pl and news_images
      if (editingNews && removedImageUrls.length > 0) {
        const urlsToDelete = removedImageUrls.filter((p) => p && (p.startsWith('http://') || p.startsWith('https://')));
        if (urlsToDelete.length > 0) await deleteImagesOnHome(urlsToDelete);
        const { error: removeError } = await supabaseAdmin
          .from('news_images')
          .delete()
          .eq('news_id', editingNews.id)
          .in('image_path', removedImageUrls);
        if (removeError) throw new Error(removeError.message || 'Błąd usuwania zdjęć z galerii');
      }

      // On EDIT: usuń z home.pl zdjęcia usunięte z treści (tokeny [image:url])
      if (editingNews && editingNews.blocks && !editingNews.parent_id) {
        const previousContentUrls = getContentImageUrlsFromBlocks(editingNews.blocks);
        const currentContentUrls = getContentImageUrlsFromBlocks(blocks);
        const removedContentUrls = previousContentUrls.filter((u) => !currentContentUrls.includes(u));
        const removedFromHome = removedContentUrls.filter((u) => u && u.includes('uploads/news'));
        if (removedFromHome.length > 0) await deleteImagesOnHome(removedFromHome);
      }

      let newsId;
      // Publikowanie szkicu powiązanego z opublikowaną aktualnością: nadpisz rodzica treścią szkicu, usuń szkic z roboczych.
      if (saveStatus === 'published' && editingNews?.parent_id) {
        const parentId = editingNews.parent_id;
        const { error: updateParentError } = await supabaseAdmin
          .from('news')
          .update({
            title: titleToSave,
            content: sanitizedContent,
            blocks: blocks,
            status: 'published',
            updated_at: new Date().toISOString(),
            send_push_on_publish: sendPushNotification,
            push_recipient_ids: (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) ? selectedPushUserIds : null,
          })
          .eq('id', parentId);
        if (updateParentError) throw new Error(updateParentError.message || 'Błąd aktualizacji opublikowanej aktualności');
        const { data: draftImages } = await supabaseAdmin.from('news_images').select('image_path').eq('news_id', editingNews.id);
        await supabaseAdmin.from('news_images').delete().eq('news_id', parentId);
        if (draftImages?.length) {
          await supabaseAdmin.from('news_images').insert(
            draftImages.map((row) => ({ news_id: parentId, image_path: row.image_path }))
          );
        }
        await supabaseAdmin.from('news_images').delete().eq('news_id', editingNews.id);
        await supabaseAdmin.from('news').delete().eq('id', editingNews.id);
        toast.success('Aktualność zaktualizowana (wersja robocza opublikowana)');
        await logAction(
          { section: SECTIONS.NEWS, action: ACTIONS.UPDATE, entityType: 'news', entityId: parentId, entityLabel: formData.title.trim(), details: 'Opublikowano wersję roboczą nadpisując opublikowaną aktualność.' },
          user
        );
        if (sendPushNotification && parentId) {
          try {
            const pushTitle = `Nowa aktualność: ${titleToSave}`;
            const pushBody = titleToSave.length > 100 ? titleToSave.slice(0, 97) + '...' : titleToSave;
            const body = {
              title: pushTitle,
              body: pushBody,
              type: 'info',
              content_type: 'news',
              data: { type: 'news', screen: 'News', newsId: String(parentId) },
            };
            if (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) {
              body.user_ids = selectedPushUserIds;
            }
            const { error: pushError } = await supabaseAdmin.functions.invoke('send-push-notification', { body });
            if (pushError) {
              console.error('Push notification error:', pushError);
              toast.error('Aktualność zapisana, ale wysyłka push nie powiodła się');
            } else {
              toast.success('Powiadomienie push wysłane');
            }
          } catch (pushErr) {
            console.error('Push notification error:', pushErr);
            toast.error('Aktualność zapisana, ale wysyłka push nie powiodła się');
          }
        }
        resetModalState();
        loadNews();
        return;
      }

      if (editingNews) {
        const wasAlreadyPublished = editingNews.status === 'published';
        const publishedAt = saveStatus === 'published'
          ? (wasAlreadyPublished ? (editingNews.published_at || new Date().toISOString()) : new Date().toISOString())
          : null;
        const { data, error } = await supabaseAdmin
          .from('news')
          .update({
            title: titleToSave,
            content: sanitizedContent,
            blocks: blocks,
            status: saveStatus,
            published_at: publishedAt,
            updated_at: new Date().toISOString(),
            send_push_on_publish: sendPushNotification,
            push_recipient_ids: (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) ? selectedPushUserIds : null,
          })
          .eq('id', editingNews.id)
          .select()
          .single();

        if (error) {
          console.error('Supabase update error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(error.message || 'Błąd podczas aktualizacji aktualności');
        }
        newsId = data.id;
        toast.success('Aktualność zaktualizowana');
        await logAction(
          {
            section: SECTIONS.NEWS,
            action: ACTIONS.UPDATE,
            entityType: 'news',
            entityId: data.id,
            entityLabel: formData.title.trim(),
            details: 'Zaktualizowano tytuł, treść lub status.',
          },
          user
        );
        // Od razu zaktualizuj listę, żeby przy ponownym otwarciu edycji były zapisane bloki (np. width zdjęć)
        setNews((prev) =>
          prev.map((n) => (n.id === data.id ? { ...n, ...data, news_images: n.news_images } : n))
        );
      } else {
        const { data, error } = await supabaseAdmin
          .from('news')
          .insert({
            title: titleToSave,
            content: sanitizedContent,
            blocks: blocks,
            status: saveStatus,
            published_at: saveStatus === 'published' ? new Date().toISOString() : null,
            send_push_on_publish: sendPushNotification,
            push_recipient_ids: (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) ? selectedPushUserIds : null,
          })
          .select()
          .single();

        if (error) {
          console.error('Supabase insert error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(error.message || 'Błąd podczas dodawania aktualności');
        }
        newsId = data.id;
        toast.success('Aktualność dodana');
        await logAction(
          {
            section: SECTIONS.NEWS,
            action: ACTIONS.CREATE,
            entityType: 'news',
            entityId: data.id,
            entityLabel: formData.title.trim(),
            details: `Dodano aktualność: "${formData.title.trim()}". Status: ${formData.status}.`,
          },
          user
        );
      }

      // Upload gallery images to home.pl, then save URLs to news_images
      // Order is preserved by DELETE + INSERT in array order (created_at will reflect order)
      if (galleryFiles.length > 0 || existingGalleryImages.length > 0) {
        setUploadingGallery(true);
        try {
          // Step 1: Upload all NEW images to home.pl
          const uploadedUrls = [];
          for (let i = 0; i < galleryFiles.length; i++) {
            try {
              const url = await uploadImageToHome(galleryFiles[i], 'news');
              uploadedUrls.push(url);
              console.log(`Uploaded image ${i + 1}/${galleryFiles.length}: ${url}`);
            } catch (uploadError) {
              console.error(`Error uploading image ${i + 1}:`, uploadError);
              throw new Error(`Błąd przesyłania obrazu ${i + 1}: ${uploadError.message}`);
            }
          }

          // Step 2: DELETE all existing news_images for this news (to preserve order via re-insert)
          if (existingGalleryImages.length > 0) {
            console.log('=== DELETING EXISTING IMAGES TO PRESERVE ORDER ===');
            const { error: deleteError } = await supabaseAdmin
              .from('news_images')
              .delete()
              .eq('news_id', newsId);
            
            if (deleteError) {
              console.error('Error deleting existing images:', deleteError);
              throw new Error(`Błąd usuwania starych obrazów: ${deleteError.message}`);
            }
            console.log(`Deleted ${existingGalleryImages.length} existing images`);
          }

          // Step 3: Re-insert ALL images in the correct order (existing first, then new)
          // image_path: existing = path or URL from DB; new = URL from home.pl
          const allImagePaths = [
            ...existingGalleryImages.map(img => img.image_path),
            ...uploadedUrls
          ];

          if (allImagePaths.length > 0) {
            // CRITICAL: Verify newsId exists before insert
            if (!newsId) {
              console.error('CRITICAL: newsId is missing! Cannot insert gallery images.');
              throw new Error('Błąd: Brak ID aktualności. Nie można zapisać galerii.');
            }

            // Insert all images - order is preserved by insert order (created_at)
            const galleryUploads = allImagePaths.map((imagePath) => ({
              news_id: newsId,
              image_path: imagePath
            }));

            console.log('=== INSERTING GALLERY IMAGES ===');
            console.log('newsId:', newsId);
            console.log('Number of images:', galleryUploads.length);

            const { data: insertData, error: galleryError } = await supabaseAdmin
              .from('news_images')
              .insert(galleryUploads)
              .select();

            if (galleryError) {
              console.error('=== news_images INSERT FAILED ===');
              console.error('Error:', galleryError);
              throw new Error(`Błąd zapisywania galerii: ${galleryError.message || 'Nieznany błąd'}`);
            }

            if (!insertData || insertData.length === 0) {
              throw new Error('Błąd: Wstawienie do bazy nie zwróciło danych.');
            }

            console.log('=== news_images INSERT SUCCESS ===');
            console.log(`Successfully inserted ${insertData.length} rows`);
            toast.success(`Galeria zapisana: ${galleryUploads.length} zdjęć`);
          }
        } catch (galleryUploadError) {
          console.error('=== GALLERY UPLOAD PROCESS FAILED ===');
          console.error('Error:', galleryUploadError);
          toast.error(galleryUploadError.message || 'Błąd podczas przesyłania galerii');
          throw galleryUploadError;
        } finally {
          setUploadingGallery(false);
        }
      }

      if (saveStatus === 'published' && sendPushNotification && newsId) {
        try {
          const pushTitle = `Nowa aktualność: ${titleToSave}`;
          const pushBody = titleToSave.length > 100 ? titleToSave.slice(0, 97) + '...' : titleToSave;
          const body = {
            title: pushTitle,
            body: pushBody,
            type: 'info',
            content_type: 'news',
            data: { type: 'news', screen: 'News', newsId: String(newsId) },
          };
          if (isAdmin && selectPushRecipients && selectedPushUserIds.length > 0) {
            body.user_ids = selectedPushUserIds;
          }
          const { error: pushError } = await supabaseAdmin.functions.invoke('send-push-notification', { body });
          if (pushError) {
            console.error('Push notification error:', pushError);
            toast.error('Aktualność zapisana, ale wysyłka push nie powiodła się');
          } else {
            toast.success('Powiadomienie push wysłane');
          }
        } catch (pushErr) {
          console.error('Push notification error:', pushErr);
          toast.error('Aktualność zapisana, ale wysyłka push nie powiodła się');
        }
      }

      resetModalState();
      loadNews();
    } catch (error) {
      console.error('=== HANDLE SUBMIT ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // If it's a gallery error, provide more context
      if (error.message && error.message.includes('galerii')) {
        console.error('=== GALLERY INSERT ERROR DETECTED ===');
        console.error('This error indicates news_images insert failed.');
        console.error('Possible causes:');
        console.error('1. RLS policy blocking INSERT');
        console.error('2. Missing news_id foreign key');
        console.error('3. Invalid image_path format');
        console.error('4. Database constraint violation');
      }
      
      toast.error(error.message || 'Błąd podczas zapisywania');
      console.error('Full error object:', error);
    }
  };

  const handleEdit = async (item) => {
    lastChangeAtRef.current = 0;
    lastAutosaveAtRef.current = 0;
    setEditingNews(item);
    setFormData({
      title: item.title,
      status: item.status,
    });
    const isPublished = item.status === 'published';
    setSendPushNotification(isPublished ? false : (item.send_push_on_publish !== false));
    setSelectPushRecipients(isPublished ? false : (Array.isArray(item.push_recipient_ids) && item.push_recipient_ids.length > 0));
    setSelectedPushUserIds(isPublished ? [] : (Array.isArray(item.push_recipient_ids) ? item.push_recipient_ids : []));
    // Load blocks from DB or convert from content for backward compatibility
    if (item.blocks && Array.isArray(item.blocks) && item.blocks.length > 0) {
      const normalizedBlocks = item.blocks.map(block => {
        if (block.type === 'text') {
          return {
            ...block,
            bold: block.bold ?? false,
            italic: block.italic ?? false,
            underline: block.underline ?? false,
            align: block.align || 'left'
          };
        } else if (block.type === 'link') {
          return {
            ...block,
            text: block.text || '',
            url: block.url || '',
            align: block.align || 'left'
          };
        } else if (block.type === 'facebookVideo') {
          return {
            type: 'facebookVideo',
            url: block.url || '',
          };
        }
        return block;
      });
      setBlocks(normalizedBlocks);
    } else {
      const contentFromDb = typeof item.content === 'string' ? item.content : '';
      const convertedBlocks = htmlToBlocks(contentFromDb);
      setBlocks(convertedBlocks);
    }
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setRemovedImageUrls([]);
    try {
      const { data: imagesData, error: imagesError } = await supabaseAdmin
        .from('news_images')
        .select('*')
        .eq('news_id', item.id);
      if (imagesError) {
        console.error('Error loading gallery images:', imagesError);
        setExistingGalleryImages([]);
      } else {
        const sorted = (imagesData || []).sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        setExistingGalleryImages(sorted);
      }
    } catch (err) {
      console.error('Error loading gallery images:', err);
      setExistingGalleryImages([]);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę aktualność?')) return;

    try {
      const { data: newsImages, error: fetchError } = await supabaseAdmin
        .from('news_images')
        .select('image_path')
        .eq('news_id', id);
      if (fetchError) throw fetchError;

      let urls = (newsImages || [])
        .map((row) => row.image_path)
        .filter((path) => path && (path.startsWith('http://') || path.startsWith('https://')));
      if (urls.length > 0) {
        const { data: otherImages } = await supabaseAdmin
          .from('news_images')
          .select('image_path')
          .neq('news_id', id);
        const usedElsewhere = new Set((otherImages || []).map((r) => r.image_path).filter(Boolean));
        urls = urls.filter((path) => !usedElsewhere.has(path));
        if (urls.length > 0) await deleteImagesOnHome(urls);
      }

      const { error } = await supabaseAdmin
        .from('news_images')
        .delete()
        .eq('news_id', id);
      if (error) throw error;

      const { error: newsError } = await supabaseAdmin
        .from('news')
        .delete()
        .eq('id', id);
      if (newsError) {
        console.error('Supabase delete error:', { code: newsError.code, message: newsError.message });
        throw new Error(newsError.message || 'Błąd podczas usuwania aktualności');
      }
      toast.success('Aktualność usunięta');
      const item = news.find((n) => n.id === id);
      await logAction(
        {
          section: SECTIONS.NEWS,
          action: ACTIONS.DELETE,
          entityType: 'news',
          entityId: id,
          entityLabel: item?.title,
          details: item?.status === 'draft' ? 'Usunięto aktualność (draft).' : 'Usunięto aktualność.',
        },
        user
      );
      loadNews();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas usuwania');
      console.error('Error deleting news:', error);
    }
  };

  const handlePublish = async (id, currentStatus, itemFromList) => {
    try {
      const item = itemFromList || news.find((n) => n.id === id);
      if (currentStatus === 'draft' && item?.parent_id) {
        const { data: draftRow, error: fetchErr } = await supabaseAdmin
          .from('news')
          .select('*')
          .eq('id', id)
          .single();
        if (fetchErr || !draftRow) throw new Error(fetchErr?.message || 'Nie znaleziono szkicu');
        const parentId = draftRow.parent_id;
        const { error: updateParentError } = await supabaseAdmin
          .from('news')
          .update({
            title: draftRow.title,
            content: draftRow.content,
            blocks: draftRow.blocks,
            status: 'published',
            updated_at: new Date().toISOString(),
            send_push_on_publish: draftRow.send_push_on_publish,
            push_recipient_ids: draftRow.push_recipient_ids,
          })
          .eq('id', parentId);
        if (updateParentError) throw updateParentError;
        const { data: draftImages } = await supabaseAdmin.from('news_images').select('image_path').eq('news_id', id);
        await supabaseAdmin.from('news_images').delete().eq('news_id', parentId);
        if (draftImages?.length) {
          await supabaseAdmin.from('news_images').insert(
            draftImages.map((row) => ({ news_id: parentId, image_path: row.image_path }))
          );
        }
        await supabaseAdmin.from('news_images').delete().eq('news_id', id);
        await supabaseAdmin.from('news').delete().eq('id', id);
        toast.success('Opublikowano (wersja robocza nadpisuje opublikowaną)');
        await logAction(
          { section: SECTIONS.NEWS, action: ACTIONS.STATUS_CHANGE, entityType: 'news', entityId: parentId, entityLabel: item?.title, details: 'Opublikowano wersję roboczą nadpisując opublikowaną aktualność.', extra: { oldStatus: 'draft', newStatus: 'published' } },
          user
        );
        const shouldSendPush = draftRow.send_push_on_publish !== false;
        if (shouldSendPush) {
          try {
            const pushTitle = `Nowa aktualność: ${draftRow.title || ''}`;
            const pushBody = (draftRow.title || '').length > 100 ? (draftRow.title || '').slice(0, 97) + '...' : (draftRow.title || '');
            const body = {
              title: pushTitle,
              body: pushBody,
              type: 'info',
              content_type: 'news',
              data: { type: 'news', screen: 'News', newsId: String(parentId) },
            };
            if (isAdmin && Array.isArray(draftRow.push_recipient_ids) && draftRow.push_recipient_ids.length > 0) {
              body.user_ids = draftRow.push_recipient_ids;
            }
            const { error: pushError } = await supabaseAdmin.functions.invoke('send-push-notification', { body });
            if (pushError) {
              console.error('Push notification error:', pushError);
              toast.error('Aktualność zapisana, ale wysyłka push nie powiodła się');
            } else {
              toast.success('Powiadomienie push wysłane');
            }
          } catch (pushErr) {
            console.error('Push notification error:', pushErr);
            toast.error('Aktualność zapisana, ale wysyłka push nie powiodła się');
          }
        }
        loadNews();
        return;
      }
      const newStatus = currentStatus === 'published' ? 'draft' : 'published';
      const publishedAt = newStatus === 'published' ? (item?.published_at || new Date().toISOString()) : null;
      const { error } = await supabaseAdmin
        .from('news')
        .update({
          status: newStatus,
          published_at: publishedAt,
        })
        .eq('id', id);

      if (error) throw error;
      toast.success(newStatus === 'published' ? 'Opublikowano' : 'Cofnięto publikację');
      await logAction(
        {
          section: SECTIONS.NEWS,
          action: ACTIONS.STATUS_CHANGE,
          entityType: 'news',
          entityId: id,
          entityLabel: item?.title,
          details: `Status: ${currentStatus} → ${newStatus}.`,
          extra: { oldStatus: currentStatus, newStatus },
        },
        user
      );
      loadNews();
    } catch (error) {
      toast.error('Błąd podczas zmiany statusu');
      console.error(error);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="news-management">
      <div className="page-header">
        <h2>Zarządzanie aktualnościami</h2>
        <button className="btn btn-primary" onClick={() => {
          lastChangeAtRef.current = 0;
          lastAutosaveAtRef.current = 0;
          setBlocks([{ 
        type: 'text', 
        text: '', 
        align: 'left',
        bold: false,
        italic: false,
        underline: false
      }]);
          setShowModal(true);
        }}>
          Dodaj aktualność
        </button>
      </div>

      <div className="tabs-row" style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        <button
          type="button"
          className={`btn ${listTab === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setListTab('all')}
        >
          Opublikowane
        </button>
        <button
          type="button"
          className={`btn ${listTab === 'drafts' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setListTab('drafts')}
        >
          Robocze
        </button>
      </div>
      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Tytuł</th>
              <th>Status</th>
              <th>Data utworzenia</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {displayedNews.length === 0 ? (
              <tr>
                <td colSpan="4" className="empty-state">
                  {listTab === 'drafts' ? 'Brak wersji roboczych' : 'Brak opublikowanych aktualności'}
                </td>
              </tr>
            ) : (
              displayedNews.map((item) => (
                <tr key={item.id}>
                  <td>{item.title}</td>
                  <td>
                    <span className={`badge ${item.status === 'published' ? 'badge-success' : 'badge-warning'}`}>
                      {item.status === 'published' ? 'Opublikowane' : 'Szkic'}
                    </span>
                  </td>
                  <td>{new Date(item.created_at).toLocaleDateString('pl-PL')}</td>
                  <td>
                    <div className="action-buttons">
                      {listTab !== 'drafts' && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => handlePublish(item.id, item.status, item)}
                        >
                          Cofnij
                        </button>
                      )}
                      <button
                        className="btn btn-primary"
                        onClick={() => handleEdit(item)}
                      >
                        Edytuj
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div 
        className="modal-overlay wide-modal-overlay" 
        style={{ display: showModal ? 'flex' : 'none' }}
        onClick={() => { if (!uploadingGallery) handleCloseModal(); }}
      >
          <div className="modal-content wide-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingNews ? 'Edytuj aktualność' : 'Dodaj aktualność'}
              </h3>
              <button
                className="modal-close"
                onClick={() => { if (!uploadingGallery) handleCloseModal(); }}
                disabled={uploadingGallery}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Tytuł *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => {
                    lastChangeAtRef.current = Date.now();
                    setFormData({ ...formData, title: e.target.value });
                  }}
                  required
                  disabled={uploadingGallery}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Treść *</label>
                <div className="editor-hints">
                  <small>
                    💡 Wskazówki: Używaj nagłówków oszczędnie • Nie wklejaj treści bezpośrednio z Word/PDF • 
                    Obrazy automatycznie dopasowują szerokość • Emoji są wspierane natywnie 🙂
                  </small>
                  {(() => {
                    const totalChars = blocks.reduce((sum, block) => {
                      return sum + (block.type === 'text' ? (block.text || '').length : 0);
                    }, 0);
                    return totalChars > 0 && (
                      <small className={totalChars > 10000 ? 'char-warning' : ''}>
                        Znaki: {totalChars} / 10 000
                      </small>
                    );
                  })()}
                </div>
                <BlockEditor
                  blocks={blocks}
                  onBlocksChange={(newBlocks) => {
                    lastChangeAtRef.current = Date.now();
                    setBlocks(newBlocks);
                  }}
                  onImageUpload={uploadImage}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Galeria zdjęć aktualności (pierwsze zdjęcie = zdjęcie główne)</label>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleGallerySelect}
                  className="form-input"
                  disabled={uploadingGallery}
                />
                {uploadingGallery && (
                  <div className="upload-loader">Przesyłanie galerii...</div>
                )}
                {(galleryPreviews.length > 0 || existingGalleryImages.length > 0) && (
                  <div className="gallery-preview-container">
                    {/* Nowe zdjęcia (przed zapisem) */}
                    {galleryPreviews.map((preview, index) => (
                      <div key={`new-${index}`} className="gallery-item">
                        <img src={preview.preview} alt={`Preview ${index + 1}`} className="gallery-preview" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeGalleryFile(index)}
                          disabled={uploadingGallery}
                        >
                          Usuń
                        </button>
                        <div className="gallery-controls">
                          <button
                            type="button"
                            className="move-btn"
                            onClick={() => moveGalleryImageUp(index, false)}
                            disabled={uploadingGallery || index === 0}
                            title="Przenieś w górę"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="move-btn"
                            onClick={() => moveGalleryImageDown(index, false)}
                            disabled={uploadingGallery || index === galleryPreviews.length - 1}
                            title="Przenieś w dół"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Istniejące zdjęcia (z bazy) */}
                    {existingGalleryImages.map((img, index) => (
                      <div key={img.id} className="gallery-item">
                        <img src={getPublicImageUrl(img.image_path)} alt="Gallery" className="gallery-preview" />
                        {index === 0 && <div className="main-image-badge">Główne</div>}
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => markGalleryImageForRemoval(img.image_path)}
                          disabled={uploadingGallery}
                        >
                          Usuń
                        </button>
                        <div className="gallery-controls">
                          <button
                            type="button"
                            className="move-btn"
                            onClick={() => moveGalleryImageUp(index, true)}
                            disabled={uploadingGallery || index === 0}
                            title="Przenieś w górę"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="move-btn"
                            onClick={() => moveGalleryImageDown(index, true)}
                            disabled={uploadingGallery || index === existingGalleryImages.length - 1}
                            title="Przenieś w dół"
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  disabled={uploadingGallery}
                >
                  <option value="draft">Szkic</option>
                  <option value="published">Opublikowane</option>
                </select>
              </div>
              <div className="form-group" style={editingNews?.status === 'published' ? { opacity: 0.65, pointerEvents: 'none' } : undefined}>
                <label className="form-label">Powiadomienie push</label>
                <label className="checkbox-label" style={{ display: 'block', marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={editingNews?.status === 'published' ? false : sendPushNotification}
                    onChange={(e) => setSendPushNotification(e.target.checked)}
                    disabled={uploadingGallery || editingNews?.status === 'published'}
                  />
                  <span>Wyślij push powiadomienie o nowej aktualności (po opublikowaniu)</span>
                </label>
                {isAdmin && (
                  <>
                    <label className="checkbox-label" style={{ display: 'block', marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={editingNews?.status === 'published' ? false : selectPushRecipients}
                        onChange={(e) => {
                          setSelectPushRecipients(e.target.checked);
                          if (!e.target.checked) setSelectedPushUserIds([]);
                        }}
                        disabled={uploadingGallery || editingNews?.status === 'published'}
                      />
                      <span>Wybierz odbiorców (domyślnie: wszyscy użytkownicy)</span>
                    </label>
                    {selectPushRecipients && !(editingNews?.status === 'published') && (
                      <div className="push-recipients-list" style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd', padding: 8, borderRadius: 4 }}>
                        <p className="form-hint" style={{ marginBottom: 8 }}>Zaznacz użytkowników, do których wysłać push:</p>
                        {pushRecipientsList.length === 0 ? (
                          <p className="info-text">Ładowanie listy...</p>
                        ) : (
                          <div className="push-recipients-checkboxes">
                            {pushRecipientsList.map((u) => (
                              <label key={u.id} className="checkbox-label push-recipient-item" style={{ display: 'block', marginBottom: 4 }}>
                                <input
                                  type="checkbox"
                                  checked={selectedPushUserIds.includes(u.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPushUserIds((prev) => [...prev, u.id]);
                                    } else {
                                      setSelectedPushUserIds((prev) => prev.filter((id) => id !== u.id));
                                    }
                                  }}
                                />
                                <span>{u.device_id || `ID: ${u.id}`}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {selectedPushUserIds.length > 0 && (
                          <p className="form-hint" style={{ marginTop: 8 }}>Wybrano: {selectedPushUserIds.length} użytkowników</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={uploadingGallery}
                >
                  {uploadingGallery ? 'Zapisywanie...' : (editingNews ? 'Zaktualizuj' : 'Dodaj')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { if (!uploadingGallery) handleCloseModal(); }}
                  disabled={uploadingGallery}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
      </div>
    </div>
  );
}
